/**
 * LegalLayout — közös wrapper a publikus jogi oldalakhoz (/aszf,
 * /adatkezelesi-tajekoztato). Tudatosan egyszerű: MEX Magic logo
 * felül, középre rendezett olvasókonténer (max-width ~880px),
 * Meta + minimal footer linksorral. NEM dashboard-stílusú.
 */
import React from "react";
import { Sparkle } from "@phosphor-icons/react";

export const LegalContainer = ({ children, testid }) => (
  <article
    data-testid={testid}
    className="mx-auto"
    style={{
      maxWidth: 880,
      padding: "32px 22px 56px",
      color: "#2B2545",
      lineHeight: 1.7,
      fontSize: 15.5,
    }}
  >
    {children}
  </article>
);

export const LegalH1 = ({ children, testid }) => (
  <h1
    data-testid={testid}
    className="font-heading"
    style={{
      fontSize: "clamp(28px, 3vw, 38px)",
      fontWeight: 700,
      lineHeight: 1.18,
      letterSpacing: "-0.02em",
      color: "#1B1733",
      marginBottom: 18,
    }}
  >
    {children}
  </h1>
);

export const LegalH2 = ({ children }) => (
  <h2
    className="font-heading"
    style={{
      fontSize: "clamp(18px, 1.5vw, 22px)",
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: "#1B1733",
      marginTop: 36,
      marginBottom: 10,
    }}
  >
    {children}
  </h2>
);

export const LegalP = ({ children }) => (
  <p style={{ marginBottom: 14, color: "#3F365E" }}>{children}</p>
);

export const LegalUL = ({ children }) => (
  <ul style={{ marginBottom: 14, paddingLeft: 22, color: "#3F365E" }}>
    {children}
  </ul>
);

export const LegalLI = ({ children }) => (
  <li style={{ marginBottom: 6, lineHeight: 1.6 }}>{children}</li>
);

export const LegalStrong = ({ children }) => (
  <strong style={{ color: "#1B1733", fontWeight: 700 }}>{children}</strong>
);

export const LegalHR = () => (
  <hr style={{ border: 0, borderTop: "1px solid rgba(94,80,247,0.18)", margin: "28px 0" }} />
);

export default function LegalLayout({ children }) {
  return (
    <div
      className="font-sans relative min-h-screen flex flex-col"
      data-font="geist"
      style={{
        background:
          "radial-gradient(60% 50% at 50% 0%, rgba(94,80,247,0.10) 0%, rgba(255,255,255,0) 60%), linear-gradient(180deg, #F8F6FE 0%, #FBFAFF 100%)",
      }}
    >
      <header
        className="w-full px-6 sm:px-10 lg:px-16 pt-7 pb-2 flex items-center justify-center md:justify-start relative z-10"
        data-testid="legal-header"
      >
        <a
          href="/"
          className="flex items-center gap-3.5"
          data-testid="legal-logo"
        >
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-[0_10px_28px_rgba(94,80,247,0.32)]"
            style={{ background: "linear-gradient(135deg, #5e50f7 0%, #3a2fd3 100%)" }}
          >
            <Sparkle size={22} weight="fill" color="#fff" />
          </div>
          <div className="font-heading text-[22px] sm:text-[26px] tracking-tight text-[#2B2545] font-bold">
            MEX <span className="text-[#5e50f7]">Magic</span>
          </div>
        </a>
      </header>

      <main className="flex-1 relative z-10 px-2 sm:px-6">{children}</main>

      <PublicFooter />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * PublicFooter — diszkrét, kis méretű link sor a publikus oldalakon.
 * Megjelenik: /, /webinar, /webinar-thankyou, /aszf, /adatkezelesi-tajekoztato.
 * ──────────────────────────────────────────────────────────────── */
export function PublicFooter({ extra }) {
  return (
    <footer
      data-testid="public-footer"
      className="w-full px-6 sm:px-10 lg:px-16 py-6 text-center"
      style={{
        background: "#EDE3F6",
        borderTop: "1px solid rgba(94,80,247,0.08)",
        color: "#6F6790",
        fontSize: 12.5,
        lineHeight: 1.6,
      }}
    >
      <div className="max-w-[1100px] mx-auto flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <a
            href="/aszf"
            data-testid="footer-link-aszf"
            className="hover:text-[#5e50f7] transition-colors"
          >
            ÁSZF
          </a>
          <span aria-hidden="true" style={{ opacity: 0.6 }}>·</span>
          <span>MEX Magic • All rights reserved.</span>
          <span aria-hidden="true" style={{ opacity: 0.6 }}>·</span>
          <a
            href="/adatkezelesi-tajekoztato"
            data-testid="footer-link-gdpr"
            className="hover:text-[#5e50f7] transition-colors"
          >
            Adatkezelési tájékoztató
          </a>
        </div>
        {extra && (
          <div style={{ fontSize: 11.5, lineHeight: 1.55, color: "#8A82A8", maxWidth: 760, marginInline: "auto" }}>
            {extra}
          </div>
        )}
      </div>
    </footer>
  );
}
